import { OverviewRoute, RoutePath } from './types';
import { Connections } from 'integrations/common/containers/connections/Connections';
import { Schedules } from 'integrations/common/containers/schedules/Schedules';
import { TransferConfigs } from 'integrations/common/containers/transferConfigs/TransferConfigs';
import { IntegrationTab } from 'integrations/common/types/common';
import { isIntegrationWithConnections } from 'integrations/common/streams/connections/utils';
import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { isIntegrationWithTransferConfigs } from '../streams/transferConfigs/utils';
import { isIntegrationWithSchedules } from '../streams/schedules/utils';
import { type I18nLocale, t } from '@ardoq/i18n';

type RouteAvailabilityArgs = {
  route: RoutePath;
  integrationId: IntegrationId;
  hasConnections: boolean;
  hasTransferConfigs: boolean;
  hasSchedules: boolean;
};

export const isOverviewRouteAvailable = ({
  route,
  integrationId,
  hasConnections,
  hasTransferConfigs,
  hasSchedules,
}: RouteAvailabilityArgs): boolean => {
  if (!isIntegrationWithConnections(integrationId)) return true;
  if (hasConnections) return true;

  switch (route) {
    case OverviewRoute.CONFIGURATIONS:
      return hasTransferConfigs;
    case OverviewRoute.SCHEDULES:
      return hasSchedules;
    default:
      return true;
  }
};

type TabConfig = Omit<IntegrationTab<OverviewRoute>, 'label'> & {
  labelKey: string;
  defaultLabel: string;
};

const tabConfigs: TabConfig[] = [
  {
    labelKey: 'integrations:overviewTabs.schedules',
    defaultLabel: 'Schedules',
    path: OverviewRoute.SCHEDULES,
    component: Schedules,
    isAccessible: isIntegrationWithSchedules,
  },
  {
    labelKey: 'integrations:overviewTabs.configurations',
    defaultLabel: 'Configurations',
    path: OverviewRoute.CONFIGURATIONS,
    component: TransferConfigs,
    isAccessible: isIntegrationWithTransferConfigs,
  },
  {
    labelKey: 'integrations:overviewTabs.connections',
    defaultLabel: 'Connections',
    path: OverviewRoute.CONNECTIONS,
    component: Connections,
    isAccessible: (integrationId: IntegrationId) =>
      isIntegrationWithConnections(integrationId),
  },
];

const toIntegrationTab = (
  i18nLocale: I18nLocale | undefined,
  { labelKey, defaultLabel, ...rest }: TabConfig
): IntegrationTab<OverviewRoute> => ({
  ...rest,
  label: i18nLocale ? t(i18nLocale, labelKey, defaultLabel) : defaultLabel,
});

type GetAllowedTabsArgs = {
  hasConnections?: boolean;
  hasTransferConfigs?: boolean;
  hasSchedules?: boolean;
  integrationId: IntegrationId;
  isOrgAdmin: boolean;
  allowedOverviewRoutes?: OverviewRoute[];
  /**
   * Required when callers render the resulting `tab.label`. Routines that
   * only read `path`/`isAccessible` (e.g. picking the default tab to
   * navigate to) may omit this; the returned label falls back to the
   * English default and is unused.
   */
  i18nLocale?: I18nLocale;
};

export const getAllowedTabs = ({
  hasConnections = true,
  hasTransferConfigs = false,
  hasSchedules = false,
  integrationId,
  isOrgAdmin,
  allowedOverviewRoutes = [
    OverviewRoute.SCHEDULES,
    OverviewRoute.CONFIGURATIONS,
    OverviewRoute.CONNECTIONS,
  ],
  i18nLocale,
}: GetAllowedTabsArgs) =>
  tabConfigs
    .filter(
      ({ isAccessible, path }) =>
        isAccessible(integrationId) &&
        allowedOverviewRoutes.includes(path) &&
        (path !== OverviewRoute.SCHEDULES || isOrgAdmin)
    )
    .map(config => toIntegrationTab(i18nLocale, config))
    .map(tab => ({
      ...tab,
      isDisabled: !isOverviewRouteAvailable({
        route: tab.path,
        integrationId,
        hasConnections,
        hasTransferConfigs,
        hasSchedules,
      }),
    }));

export const isAllowedTab = (
  path: string,
  integrationId: IntegrationId,
  isOrgAdmin: boolean,
  allowedOverviewRoutes?: OverviewRoute[]
) => {
  if (path === OverviewRoute.SCHEDULES && !isOrgAdmin) return false;
  const tab = tabConfigs
    .filter(({ path }) =>
      (allowedOverviewRoutes || Object.values(OverviewRoute)).includes(path)
    )
    .find(tab => tab.path === path);
  return tab?.isAccessible ? tab.isAccessible(integrationId) : false;
};
